import { useField, useFormikContext } from 'formik';
import React__default, { useCallback } from 'react';
import { DateInput } from '../inputs/DateInput.js';
import { setDisabledDataAttribute } from '../inputs/InputCommon.js';
import { Stack } from '../layout/Stack.js';
import { setErrorDataAttribute, FieldErrorMessage } from './FieldError.js';

/**
 * Date Input with Formik Field bindings.
 *
 * @example
 * <DateField name="scheduled" />
 */ const DateFieldAs = ({ children , disabled =false , error , name , reserveMessageSpace =true , onFieldChange , onFieldBlur , ...props })=>{
    const [field, meta] = useField(name);
    const formik = useFormikContext();
    const onChange = useCallback((v)=>{
        if (onFieldChange !== undefined) {
            onFieldChange(v);
        }
        formik.setFieldTouched(name, true, true);
        // Manually update the formik values as a workaround to formik removing the field from values completely
        // if setting the field value to undefined via the 'standard' field helpers.
        // This is a bug in formik, see https://github.com/jaredpalmer/formik/issues/2523#issuecomment-1443473902
        if (typeof formik.values === "object") {
            formik.setValues({
                ...formik.values,
                [name]: v
            });
        } else {
            formik.setValues({
                [name]: v
            });
        }
    }, [
        formik,
        name,
        onFieldChange
    ]);
    const onBlur = useCallback((v)=>{
        if (onFieldBlur !== undefined) {
            onFieldBlur(v);
        }
        formik.setFieldTouched(name, true, true);
    }, [
        formik,
        name,
        onFieldBlur
    ]);
    return React__default.createElement(Stack, {
        "data-hpy-web-date-field": true,
        ...setDisabledDataAttribute(disabled),
        ...setErrorDataAttribute(meta.touched && !!meta.error || !!error),
        gap: 0.25
    }, React__default.createElement(DateInput, {
        ...props,
        disabled: disabled,
        hasError: meta.touched && !!meta.error || !!error,
        value: field.value,
        onChange: onChange,
        onBlur: onBlur
    }, children), React__default.createElement(FieldErrorMessage, {
        name: name,
        reserveMessageSpace: reserveMessageSpace,
        error: error
    }));
};
DateFieldAs.dataId = "data-hpy-web-date-field";
DateFieldAs.displayName = "DateFieldAs";
const isBase = (props)=>props?.as === undefined;
const isButton = (props)=>props?.as === "button";
const isButtonIcon = (props)=>props?.as === "buttonIcon";
const isChip = (props)=>props?.as === "chip";
/**
 * Date Input with Formik Field bindings.
 *
 * @example
 * <DateField name="scheduled" />
 */ const DateField = ({ children , ...props })=>{
    if (isBase(props)) {
        return React__default.createElement(DateFieldAs, {
            ...props
        }, children);
    }
    if (isButton(props)) {
        return React__default.createElement(DateFieldAs, {
            ...props
        }, children);
    }
    if (isButtonIcon(props)) {
        return React__default.createElement(DateFieldAs, {
            ...props
        }, children);
    }
    if (isChip(props)) {
        return React__default.createElement(DateFieldAs, {
            ...props
        }, children);
    }
    return null;
};
DateField.dataId = "data-hpy-web-date-field";
DateField.displayName = "DateField";

export { DateField };
